'use client';

import { faAngleDown, faBarsFilter, faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, InputField, Menu, Select } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { Button as CustomButton } from '@/components/atoms/Button';
import { MobilePlayerFilters } from '@/components/molecules/MobilePlayerFilters';
import { SkillRange } from '@/components/molecules/SkillRange';
import { getLastPathSegment, pageRoutes } from '@/components/organisms/SpectatorsPagesFilterWrapper';
import { Options } from '@/utils/types';

export const getNewSearchParams = (filters: ISpectatorPagesFilter) => {
	const newSearchParams = new URLSearchParams(window.location.search);

	newSearchParams.delete('current_page');

	if (filters.search) {
		newSearchParams.set('search', filters.search);
	} else {
		newSearchParams.delete('search');
	}

	if (filters.age) {
		newSearchParams.set('age', filters.age);
	} else {
		newSearchParams.delete('age');
	}

	if (filters.gender) {
		newSearchParams.set('gender', filters.gender);
	} else {
		newSearchParams.delete('gender');
	}

	if (filters.skill) {
		newSearchParams.set('skill', filters.skill);
	} else {
		newSearchParams.delete('skill');
	}

	if (filters.singles_skill) {
		newSearchParams.set('singles_skill', filters.singles_skill);
	} else {
		newSearchParams.delete('singles_skill');
	}

	if (filters.doubles_skill) {
		newSearchParams.set('doubles_skill', filters.doubles_skill);
	} else {
		newSearchParams.delete('doubles_skill');
	}

	if (filters.division) {
		newSearchParams.set('division', filters.division);
	} else {
		newSearchParams.delete('division');
	}

	if (filters.district) {
		newSearchParams.set('district', filters.district);
	} else {
		newSearchParams.delete('district');
	}

	if (filters.player_status) {
		newSearchParams.set('show_free_agents', filters.player_status);
	} else {
		newSearchParams.delete('show_free_agents');
	}

	if (filters.team) {
		newSearchParams.set('team', filters.team);
	} else {
		newSearchParams.delete('team');
	}

	if (filters.season) {
		newSearchParams.delete('startDate');
		newSearchParams.delete('endDate');
		newSearchParams.delete('month');
		newSearchParams.set('season', filters.season);
	} else {
		newSearchParams.delete('season');
	}

	return newSearchParams || '';
};

export const AgeFilterOptions = [
	{ label: 'All', value: [] },
	{ label: '18u', value: [0, 17] },
	{ label: '18-29', value: [18, 29] },
	{ label: '30-39', value: [30, 39] },
	{ label: '40-49', value: [40, 49] },
	{ label: '50-59', value: [50, 59] },
	{ label: '60-69', value: [60, 69] },
	{ label: '70-79', value: [70, 79] },
	{ label: '80+', value: [80, 100] }
] as const;

export const GenderFilterOptions = [
	{ label: 'All', value: '' },
	{ label: 'Male', value: 'M' },
	{ label: 'Female', value: 'F' }
] as const;

export interface ISpectatorPagesFilter {
	age: string;
	doubles_skill: string;
	gender: string;
	mobile_filters: string;
	search: string;
	singles_skill: string;
	skill: string;
	season: string;
	team: string;
	division: string;
	district: string;
	player_status: string;
}

export const pagesWithFilterBar: string[] = Object.values(pageRoutes);

export const pagesWithDivisionDropdown = [pageRoutes.TEAMS, pageRoutes.PLAYERS];
export const pagesWithSeasonDropdown = [pageRoutes.STANDINGS, pageRoutes.GAMEDAY];
export const pagesWithTeamsDropdown = [pageRoutes.STANDINGS];
export const pagesWithDistrictDropdown = [pageRoutes.PLAYERS];
export const pagesWithPlayerStatusDropdown = [pageRoutes.PLAYERS];
export const pagesWithAgeFilter: string[] = [pageRoutes.PLAYERS];
export const pagesWithGenderFilter: string[] = [pageRoutes.PLAYERS];
/* export const pagesWithDateFilter: string[] = [pageRoutes.GAMEDAY]; */
export const pagesWithGamedayTeamsDropdown = [pageRoutes.GAMEDAY, pageRoutes.EVENT];
export const pagesWithSingleRatingFilter: string[] = [pageRoutes.PLAYERS];
export const pagesWithDoubleRatingFilter: string[] = [pageRoutes.PLAYERS];

export const filterButtonConfig: Record<string, 'both' | 'resetOnly'> = {
	[pageRoutes.STANDINGS]: 'both',
	[pageRoutes.TEAMS]: 'both',
	[pageRoutes.PLAYERS]: 'both',
	[pageRoutes.GAMEDAY]: 'both',
	[pageRoutes.EVENT]: 'both',
	[pageRoutes.DIVISIONS]: 'resetOnly'
};
interface SpectatorPagesFilterProps {
	teams: Options[];
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	useScheduleGroups: boolean;
	activeSeason?: string;
}

export const transformAndFilterTeams = (teams: Options[]): { value: string; label: string }[] => {
	const listOfTeams = teams
		.filter((team) => team.valueGameday !== null && team.valueGameday !== undefined)
		.map((filteredTeam) => ({
			value: filteredTeam.valueGameday!,
			label: filteredTeam.label
		}));

	const modifiedListOfTeams = listOfTeams.length >= 1 ? [{ value: '', label: 'All teams' }, ...listOfTeams] : [];
	return modifiedListOfTeams;
};

export const SpectatorPagesFilter = ({ teams, seasons, divisions, districts, useScheduleGroups, activeSeason }: SpectatorPagesFilterProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const divisionsWithAllOption: Options[] = divisions.length >= 1 ? [{ value: '', label: 'All divisions' }, ...divisions] : [];
	const districtsWithAllOption: Options[] = districts.length >= 1 ? [{ value: '', label: 'All districts' }, ...districts] : [];
	const teamsWithAllOption: Options[] = teams.length >= 1 ? [{ value: '', label: 'All teams' }, ...teams] : [];
	const playerStatusWithAllPlayersOption: Options[] = [
		{ value: '', label: 'All players' },
		{ value: 'false', label: 'On a Roster only' },
		{ value: 'true', label: 'Free Agent only' }
	];

	const currentRoute = getLastPathSegment(pathname);
	const [prevRoute, setPrevRoute] = useState(currentRoute);

	/* const [date, setDate] = React.useState<string>(); */
	const [filters, setFilters] = React.useState<ISpectatorPagesFilter>({
		age: searchParams?.get('age') ?? '',
		doubles_skill: searchParams?.get('doubles_skill') ?? '',
		gender: searchParams?.get('gender') ?? '',
		mobile_filters: '',
		search: searchParams?.get('search') ?? '',
		singles_skill: searchParams?.get('singles_skill') ?? '',
		skill: searchParams?.get('skill') ?? '',
		season:
			pageRoutes.STANDINGS === currentRoute || pageRoutes.GAMEDAY === currentRoute ? (searchParams?.get('season') ?? activeSeason ?? '') : '',
		division: searchParams?.get('division') ?? '',
		district: searchParams?.get('district') ?? '',
		player_status: searchParams?.get('show_free_agents') ?? '',
		team: searchParams?.get('team') ?? ''
	});

	const emptyFilters: ISpectatorPagesFilter = {
		age: '',
		doubles_skill: '',
		gender: '',
		mobile_filters: '',
		search: '',
		singles_skill: '',
		skill: '',
		season: pageRoutes.STANDINGS === currentRoute || pageRoutes.GAMEDAY === currentRoute ? (activeSeason ?? '') : '',
		division: '',
		district: '',
		team: '',
		player_status: ''
	};

	useEffect(() => {
		if (currentRoute !== prevRoute) {
			setFilters(emptyFilters);

			if (currentRoute !== '/gameday') {
				router.replace(pathname);
			}
			setPrevRoute(currentRoute);
		}
	}, [currentRoute, prevRoute, pathname, router]);

	const search = filters.search;
	const age =
		AgeFilterOptions.find((option) => {
			const split = filters.age ? filters.age.split('-').map((s) => Number(s)) : '';
			return split && split[0] === option.value[0] && split[1] === option.value[1];
		}) ?? AgeFilterOptions[0];
	const gender = GenderFilterOptions.find((option) => option.value === filters.gender) ?? GenderFilterOptions[0];

	const skill = filters.skill ? filters.skill.split('-').map((s) => Number(s)) : [0.0, 8.0];
	const singlesSkill = filters.singles_skill ? filters.singles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];
	const doublesSkill = filters.doubles_skill ? filters.doubles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];

	const handleReset = () => {
		setFilters(emptyFilters);

		router.replace('?');
	};

	return (
		<div>
			<div className={`hidden lg:grid lg:grid-cols-3 gap-6`}>
				{pagesWithDistrictDropdown.includes(currentRoute) && districts.length > 1 && (
					<div className="mt-6 w-full">
						<Select
							options={districtsWithAllOption}
							selectTheme="orange"
							id="district-select"
							className="w-full border !rounded-md !border-gray-300"
							value={districtsWithAllOption.find(({ value }) => value === filters.district) || districtsWithAllOption[0]}
							onChange={(option) => {
								setFilters({ ...filters, district: (option as Options).value });
							}}
						/>
					</div>
				)}

				{pagesWithDivisionDropdown.includes(currentRoute) && divisions.length > 1 && (
					<div className="mt-6 w-full">
						<Select
							options={divisionsWithAllOption}
							selectTheme="orange"
							id="division-select"
							className="w-full border !rounded-md !border-gray-300"
							value={divisionsWithAllOption.find(({ value }) => value === filters.division) || divisionsWithAllOption[0]}
							onChange={(option) => {
								setFilters({ ...filters, division: (option as Options).value });
							}}
						/>
					</div>
				)}

				{pagesWithPlayerStatusDropdown.includes(currentRoute) && (
					<div className="mt-6 w-full">
						<Select
							options={playerStatusWithAllPlayersOption}
							placeholder="All players"
							selectTheme="orange"
							id="player-status-select"
							className="w-full border !rounded-md !border-gray-300"
							value={
								playerStatusWithAllPlayersOption.find(({ value }) => value === filters.player_status) ||
								playerStatusWithAllPlayersOption[0]
							}
							onChange={(option) => {
								setFilters({ ...filters, player_status: (option as Options).value });
							}}
						/>
					</div>
				)}
				{pagesWithSeasonDropdown.includes(currentRoute) && (
					<div className="mt-6 w-full">
						<Select
							options={seasons}
							selectTheme="orange"
							id="seasons-select"
							className="w-full border !rounded-md !border-gray-300"
							value={seasons.find(({ value }) => value === filters.season) || seasons[0]}
							onChange={(option) => {
								setFilters({ ...filters, season: (option as Options).value });
							}}
						/>
					</div>
				)}
				{pagesWithTeamsDropdown.includes(currentRoute) && (
					<div className="mt-6 w-full">
						<Select
							options={teamsWithAllOption}
							selectTheme="orange"
							id="teams-select"
							className="w-full border !rounded-md !border-gray-300"
							value={teamsWithAllOption.find(({ value }) => value === filters.team) || teamsWithAllOption[0]}
							onChange={(option) => {
								setFilters({ ...filters, team: (option as Options).value });
							}}
						/>
					</div>
				)}
			</div>
			<div>
				{pagesWithFilterBar.includes(currentRoute) && (
					<>
						<div className="flex w-full justify-center border-b py-6">
							<div className="flex w-full max-w-screen-xl flex-col items-center justify-between gap-2 px-0 lg:flex-row">
								<div
									className={clsx(
										'flex w-full gap-2',
										pageRoutes.DIVISIONS === currentRoute ||
											pageRoutes.GAMEDAY === currentRoute ||
											pageRoutes.EVENT === currentRoute
											? 'justify-between lg:max-w-full'
											: 'lg:max-w-[414px]'
									)}
								>
									<span
										className={`relative w-full ${((pageRoutes.GAMEDAY !== currentRoute && pageRoutes.EVENT !== currentRoute) || (useScheduleGroups && pageRoutes.EVENT !== currentRoute)) && 'lg:max-w-[414px]'}`}
										data-testid="players-search"
									>
										<InputField
											focusColor="orange"
											type="text"
											placeholder="Search"
											value={search}
											onChange={(e) => setFilters({ ...filters, search: e.target.value })}
											onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
												if (event.key === 'Enter') {
													router.replace(`?${getNewSearchParams(filters)?.toString()}`);
													event.currentTarget.blur();
												}
											}}
											className="border-1 h-[44px] w-full !rounded-md !border-gray-300 pl-6"
											maxLength={100}
											data-testid="players-search-field"
										/>
										<FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
									</span>
									<Button
										variant="primary"
										className={clsx(
											`!hover:bg-orange-700 !flex !bg-orange-500 hover:!bg-orange-700 !text-white !shadow-[0_0_0_0]`,
											pageRoutes.DIVISIONS === currentRoute ? 'lg:!flex' : 'lg:!hidden'
										)}
										onClick={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)}
										data-testid="players-button-search"
									>
										Search
									</Button>
								</div>

								<Button
									variant="secondary"
									className={clsx(
										'animate-fade-in-down flex h-[44px] w-full items-center justify-center bg-white lg:!hidden',
										pageRoutes.DIVISIONS === currentRoute && 'hidden'
									)}
									onClick={() => setFilters({ ...filters, mobile_filters: 'true' })}
									data-testid="players-button-filter-players"
								>
									<FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
									Filters
								</Button>

								<MobilePlayerFilters
									currentRoute={currentRoute}
									filters={filters}
									setFilters={setFilters}
									gender={gender}
									handleReset={handleReset}
									seasons={seasons}
									teams={teams}
									divisions={divisions}
									districts={districts}
									divisionsWithAllOption={divisionsWithAllOption}
									districtsWithAllOption={districtsWithAllOption}
									teamsWithAllOption={teamsWithAllOption}
									playerStatusWithAllPlayersOption={playerStatusWithAllPlayersOption}
									useScheduleGroups={useScheduleGroups}
								/>
								<div className="hidden h-[44px] gap-2 lg:flex">
									{pagesWithAgeFilter.includes(currentRoute) && (
										<Menu width={160}>
											<Menu.Trigger asChild>
												<Button
													variant="secondary"
													className={
														'!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !font-normal !text-gray-500 transition-all'
													}
													suffixIcon={<FontAwesomeIcon icon={faAngleDown} />}
													data-testid="players-age-button"
												>
													Age
												</Button>
											</Menu.Trigger>
											<Menu.List className="animate-fade-in-down w-fit min-w-[200px] rounded-lg border border-gray-300 bg-white drop-shadow-md">
												{AgeFilterOptions.map((option) => {
													return (
														<Menu.Item
															key={option.label}
															isActive={option.value === age.value}
															onClick={() => setFilters({ ...filters, age: option.value.join('-') })}
															data-testid={`players-age-option-${option.label}`}
														>
															{option.label}
														</Menu.Item>
													);
												})}
											</Menu.List>
										</Menu>
									)}

									{pagesWithGenderFilter.includes(currentRoute) && (
										<Menu width={160}>
											<Menu.Trigger asChild>
												<Button
													variant="secondary"
													className={
														'!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !font-normal !text-gray-500 transition-all'
													}
													suffixIcon={<FontAwesomeIcon icon={faAngleDown} />}
													data-testid="players-gender-button"
												>
													Gender
												</Button>
											</Menu.Trigger>

											<Menu.List className="animate-fade-in-down w-fit min-w-[200px] rounded-lg border border-gray-300 bg-white drop-shadow-md">
												{GenderFilterOptions.map((option) => {
													return (
														<Menu.Item
															key={option.value}
															isActive={option.value === gender.value}
															onClick={() => setFilters({ ...filters, gender: option.value })}
															data-testid={`players-gender-option-${option.label}`}
														>
															{option.label}
														</Menu.Item>
													);
												})}
											</Menu.List>
										</Menu>
									)}

									{pagesWithSingleRatingFilter.includes(currentRoute) && (
										<SkillRange
											setSkill={(values: any) => {
												if (values[0] === 0 && values[1] === 8) {
													setFilters({ ...filters, singles_skill: '' });
												} else {
													setFilters({ ...filters, singles_skill: values.join('-') });
												}
											}}
											skill={singlesSkill}
											filterName="Single Rating"
										/>
									)}

									{pagesWithDoubleRatingFilter.includes(currentRoute) && (
										<SkillRange
											setSkill={(values: any) => {
												if (values[0] === 0 && values[1] === 8) {
													setFilters({ ...filters, doubles_skill: '' });
												} else {
													setFilters({ ...filters, doubles_skill: values.join('-') });
												}
											}}
											skill={doublesSkill}
											filterName="Double Rating"
										/>
									)}

									{/* {pagesWithDateFilter.includes(currentRoute) && (
										<DatePicker
											label="startDate"
											selected={date ? new Date(date) : null}
											placement="bottom"
											dateFormat="dd MMM yyyy"
											onChange={(date) => setDate(dayjs(date).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'))}
											wrapperZIndex="50"
											className="!h-[44px]"
										>
											<div className="flex items-center gap-3 text-nowrap rounded-lg bg-white px-3 py-2 text-sm font-semibold leading-5 text-gray-600">
												<FontAwesomeIcon icon={faCalendar} className="" />
												<span>{date ? dayjs(date).format('MMM DD YYYY') : 'Select Date'}</span>
											</div>
										</DatePicker>
									)} */}

									{((pageRoutes.GAMEDAY === currentRoute && !useScheduleGroups) || pageRoutes.EVENT === currentRoute) && (
										<div className="w-[300px] !h-full">
											<Select
												options={transformAndFilterTeams(teams)}
												selectTheme="orange"
												className="w-full border !rounded-md !border-gray-300"
												value={
													transformAndFilterTeams(teams).find(({ value }) => value === filters.team) ||
													(transformAndFilterTeams(teams).length > 0 && transformAndFilterTeams(teams)[0])
												}
												onChange={(option) => {
													setFilters({ ...filters, team: (option as Options).value });
												}}
											/>
										</div>
									)}
								</div>
								{!!filterButtonConfig[currentRoute] && (
									<div className="hidden animate-fade-in gap-2 lg:flex">
										{filterButtonConfig[currentRoute] === 'both' && (
											<CustomButton
												className="h-11 bg-orange-500 font-semibold text-white hover:bg-orange-700"
												onClick={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)}
												data-testid="players-button-apply"
											>
												Apply
											</CustomButton>
										)}
										<Button
											variant="secondary"
											className="bg-white"
											onClick={handleReset}
											size="lg"
											data-testid="players-button-reset"
										>
											Reset
										</Button>
									</div>
								)}
							</div>
						</div>
						<div className="container mx-auto hidden max-w-screen-xl px-6 pt-2 lg:flex">
							{age && age.value.length > 0 ? (
								<span className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
									{AgeFilterOptions.find((option) => age.value === option.value)?.label} years
									<span
										className="ml-2 cursor-pointer text-gray-400 hover:text-black"
										onClick={() => setFilters({ ...filters, age: '' })}
									>
										<FontAwesomeIcon icon={faTimes} />
									</span>
								</span>
							) : null}
							{gender && gender.value !== '' ? (
								<span className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
									{`${gender.value === 'M' ? 'Male' : 'Female'}`}
									<span
										className="ml-2 cursor-pointer text-gray-400 hover:text-black"
										onClick={() => setFilters({ ...filters, gender: '' })}
									>
										<FontAwesomeIcon icon={faTimes} />
									</span>
								</span>
							) : null}

							{skill.length > 0 && ((skill[0] && skill[0] > 0) || (skill[1] && skill[1] < 8)) ? (
								<span className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
									{`Rating: ${skill[0]?.toFixed(2)} - ${skill[1]?.toFixed(2)}`}
									<span
										className="ml-2 cursor-pointer text-gray-400 hover:text-black"
										onClick={() => setFilters({ ...filters, skill: '' })}
									>
										<FontAwesomeIcon icon={faTimes} />
									</span>
								</span>
							) : null}
							{singlesSkill.length > 0 && ((singlesSkill[0] && singlesSkill[0] > 0) || (singlesSkill[1] && singlesSkill[1] < 8)) ? (
								<span className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
									{`Single Rating: ${singlesSkill[0]?.toFixed(2)} - ${singlesSkill[1]?.toFixed(2)}`}
									<span
										className="ml-2 cursor-pointer text-gray-400 hover:text-black"
										onClick={() => setFilters({ ...filters, singles_skill: '' })}
									>
										<FontAwesomeIcon icon={faTimes} />
									</span>
								</span>
							) : null}
							{doublesSkill.length > 0 && ((doublesSkill[0] && doublesSkill[0] > 0) || (doublesSkill[1] && doublesSkill[1] < 8)) ? (
								<span className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
									{`Double Rating: ${doublesSkill[0]?.toFixed(2)} - ${doublesSkill[1]?.toFixed(2)}`}
									<span
										className="ml-2 cursor-pointer text-gray-400 hover:text-black"
										onClick={() => setFilters({ ...filters, doubles_skill: '' })}
									>
										<FontAwesomeIcon icon={faTimes} />
									</span>
								</span>
							) : null}
						</div>
					</>
				)}
			</div>
		</div>
	);
};
